import { PointTran, PointsRecalculationRow } from '@app/interfaces/pointTran';
import { CustomerDataArray, CustomerModel, CustomerQueryParams } from '@app/interfaces/customer';
import { axiosApi } from './axios.api';
import { apiErrorMessage } from './ApiError';

const url = `/api/customers`;

export const getCustomersApi = async (params: CustomerQueryParams): Promise<CustomerDataArray | undefined> => {
  try {
    const response = await axiosApi.get<CustomerDataArray>(url, { params });
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const getCustomerApi = async (customerId: number) => {
  try {
    const response = await axiosApi.get(`${url}/${customerId}`);
    return response.data;

  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const getCustomerPointsHistoryApi = async (customerId: number): Promise<PointTran[]> => {
  try {
    const response = await axiosApi.get<{ rows: PointTran[] }>(`${url}/${customerId}/points_history`);
    return response.data.rows;
  } catch (error: any) {
    apiErrorMessage(error);
    return [];
  }
};

export const getPointsRecalculationApi = async (customerId: number): Promise<PointsRecalculationRow[]> => {
  try {
    const response = await axiosApi.get<{ rows: PointsRecalculationRow[] }>(`${url}/${customerId}/points_recalculation`);
    return response.data.rows;
  } catch (error: any) {
    apiErrorMessage(error);
    return [];
  }
};

export const recalculatePointsApi = async (customerId: number): Promise<void> => {
  try {
    await axiosApi.post(`${url}/${customerId}/recalculate_points`);
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const createCustomerApi = async (data: CustomerModel | FormData) => {
  try {
    const response = await axiosApi.post(url, data);
    return response.data;
   } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const updateCustomerApi = async (customerId: number, formData: CustomerModel | FormData) => {
  try {
    const response = await axiosApi.put(`${url}/${customerId}`, formData);
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const deleteCustomerApi = async (customerId: number) => {
  try {
    const response = await axiosApi.delete(`${url}/${customerId}`);
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const exportCustomerApi = async (params?: Record<string, unknown>) => {
  try {
    const response = await axiosApi.get(`${url}/export`, { params, responseType: 'blob' });

    const newUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = newUrl;
    link.setAttribute('download', `customers.xlsx`);
    document.body.appendChild(link);
    link.click();
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const exportDebitDetailCustomerApi = async (params?: Record<string, unknown>) => {
  try {
    const response = await axiosApi.get(`${url}/export_debit_detail`, { params, responseType: 'blob' });

    const newUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = newUrl;
    link.setAttribute('download', `customers_debit_detail.xlsx`);
    document.body.appendChild(link);
    link.click();
  } catch (error: any) {
    apiErrorMessage(error);
  }
};


export const getCustomerFiltersApi = async (params: any) => {
  try {
    //const response = await axios.get(`${url}/filters`, params);
    const response = await axiosApi.get(`${url}/filters`, params);

    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const getCustomersBirthdayApi = async (fromDate: Date, toDate: Date): Promise<CustomerModel[] | undefined> => {
  try {
    const response = await axiosApi.get<CustomerModel[]>(`${url}/birthday`, {
      params: {
        from_date: fromDate,
        to_date: toDate
      }
    });
    return response.data;

  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const assignCustomerApi = async (customerId: number, formData: any) => {
  try {
    const response = await axiosApi.post(`${url}/${customerId}/assign`, formData);
    return response.data;

  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const getCustomerDashboardApi = async (params?: { from_date?: string; to_date?: string }) => {
  try {
    const response = await axiosApi.get(`${url}/dashboard`, { params });
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const getCustomerDebtChartApi = async (params?: { from_date?: string; to_date?: string; limit?: number }) => {
  try {
    const response = await axiosApi.get(`${url}/debt_chart`, { params });
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const importCustomerApi = async (formData: FormData) => {
  try {
    const response = await axiosApi.post(`${url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error); // import trả JSON, không cần xử lý blob
  }
};

export const downloadImportTemplateApi = async () => {
  try {
    const response = await axiosApi.get(`${url}/download_import_template`, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', 'customer_import_template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error: any) {
    // responseType 'blob' trả error body dạng Blob, cần parse text trước khi đọc message
    if (error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      try {
        const json = JSON.parse(text);
        throw new Error(json.error || json.message || 'Lỗi tải file mẫu');
      } catch {
        throw new Error('Lỗi tải file mẫu');
      }
    }
    apiErrorMessage(error);
  }
};

export const recalculateCustomerDebitApi = async (customerId: number) => {
  try {
    const response = await axiosApi.post(`${url}/${customerId}/recalculate_debit`);
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const transferCustomerApi = async (
  customerId: number,
  targetCustomerId: number,
  deleteSource: boolean
) => {
  try {
    const response = await axiosApi.post(`${url}/${customerId}/transfer`, {
      target_customer_id: targetCustomerId,
      delete_source: deleteSource,
    });
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const getListAccountApi = async () => {
  try {
    const response = await axiosApi.get(`${url}/list_account`);
    return response.data;

  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const bulkAssignCustomerApi = async (formData: {
  user_names: string[];
  override: boolean;
  customer_group_id?: number;
  customer_ids?: number[];
}) => {
  try {
    const response = await axiosApi.post(`${url}/bulk_assign`, formData);
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const searchCustomersApi = async (query: string) => {
  try {
    const response = await axiosApi.get(url, {
      params: { name_or_phone_or_customer_code_cont: query, per_page: 30 },
    });
    return (response.data?.rows ?? []).map((item: any) => item.attributes);
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const searchCustomersForOrderApi = async (query: string): Promise<any[]> => {
  try {
    const response = await axiosApi.get(`${url}/search_for_order`, {
      params: { name_or_phone_or_customer_code_cont: query, per_page: 20 },
    });
    return (response.data?.rows ?? []).map((item: any) => item.attributes);
  } catch (error: any) {
    apiErrorMessage(error);
    return [];
  }
};

